import React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  RealSaving,
  realSavingsThreeGb,
  realSavingsFiveGb,
  realSavingsFifteenGb,
  realSavingsUnlimitedGb,
} from "./real-savings-data";
import { getRealSavingsColumns } from "./real-savings-columns";

import { TabsContent } from "app/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "app/components/ui/table";

interface Props {
  tab: string;
}

export const RealSavingsTab = ({ tab }: Props) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const data =
    tab === "3gb"
      ? realSavingsThreeGb
      : tab === "5gb"
      ? realSavingsFiveGb
      : tab === "15gb"
      ? realSavingsFifteenGb
      : tab === "unlimited"
      ? realSavingsUnlimitedGb
      : [];
  const columns = getRealSavingsColumns();

  const table = useReactTable<RealSaving>({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <TabsContent
      value={tab}
      className="w-full mt-4 sm:mt-6 md:mt-8 lg:mt-[2.31rem] mx-auto overflow-x-scroll [&::-webkit-scrollbar]:hidden"
    >
      <Table className="w-[calc(100%-2.5rem)] sm:w-[calc(100%-3rem)] md:w-[calc(100%-3.5rem)] lg:w-[calc(100%-4.5rem)] 2xl:w-[calc(100%-6rem)] mx-5 sm:mx-6 md:mx-7 lg:mx-9 xl:mx-12">
        <TableHeader className="bg-[#070816] hover:bg-black border-b border-[#1D1D1B]">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="bg-[#070816] hover:bg-black"
            >
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="h-10 sm:h-11 md:h-12 lg:h-[3.25rem]"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                className={`border-[#E2E2E2] ${
                  index === table.getRowModel().rows.length - 1
                    ? "bg-[#F0F0F07D] hover:bg-[#F0F0F0B3]"
                    : ""
                }`}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="h-80 text-center text-[#1D1D1B] text-xs sm:text-[0.825rem] lg:text-sm font-normal leading-4 sm:leading-[1.125rem] lg:leading-5"
              >
                No Available Real Savings!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TabsContent>
  );
};
